/**
 * Layout Engine — describes layouts, never draws them.
 *
 * A layout preset is a declarative description of a flyer composition:
 * fractional zones (hero, content, offer, footer), image/text zones,
 * safe areas, and per-zone alignment / spacing / decoration rules.
 *
 * resolveLayout() turns fractions into absolute rects for a given canvas,
 * which is the only geometry the rest of the engine works with.
 */

/** Zone roles understood by the grammar and composition engines. */
export const ZONE_ROLES = ['hero', 'content', 'offer', 'footer'];

const pct = (v) => Math.max(0, Math.min(1, v));

/**
 * Layout presets. All geometry is fractional (0–1) so a preset works at any
 * canvas size. `safeArea` is the margin fraction that content must respect.
 * `decorationSlots` names where the decoration engine may place ornaments.
 */
export const LAYOUT_PRESETS = {
  'classic-flyer': {
    id: 'classic-flyer',
    name: 'Classic Flyer',
    description: 'Top hero, centered content, offer band, slim footer.',
    safeArea: 0.06,
    zones: {
      hero: { x: 0, y: 0, width: 1, height: 0.38, kind: 'text', align: 'center' },
      content: { x: 0.08, y: 0.4, width: 0.84, height: 0.3, kind: 'text', align: 'center' },
      offer: { x: 0.14, y: 0.72, width: 0.72, height: 0.16, kind: 'text', align: 'center' },
      footer: { x: 0, y: 0.9, width: 1, height: 0.1, kind: 'text', align: 'center' },
    },
    imageZones: [{ x: 0, y: 0, width: 1, height: 0.38, role: 'hero-backdrop' }],
    decorationSlots: ['top-left', 'top-right', 'behind-hero', 'bottom-edge'],
  },
  'split-hero': {
    id: 'split-hero',
    name: 'Split Hero',
    description: 'Left text column, right image column, offer under text.',
    safeArea: 0.07,
    zones: {
      hero: { x: 0.07, y: 0.1, width: 0.42, height: 0.42, kind: 'text', align: 'left' },
      content: { x: 0.07, y: 0.54, width: 0.42, height: 0.2, kind: 'text', align: 'left' },
      offer: { x: 0.07, y: 0.76, width: 0.36, height: 0.12, kind: 'text', align: 'left' },
      footer: { x: 0, y: 0.92, width: 1, height: 0.08, kind: 'text', align: 'center' },
    },
    imageZones: [{ x: 0.54, y: 0.08, width: 0.4, height: 0.76, role: 'feature-image' }],
    decorationSlots: ['top-right', 'bottom-left', 'right-edge'],
  },
  'centered-poster': {
    id: 'centered-poster',
    name: 'Centered Poster',
    description: 'Everything on the vertical axis, big hero, minimal footer.',
    safeArea: 0.08,
    zones: {
      hero: { x: 0.1, y: 0.16, width: 0.8, height: 0.34, kind: 'text', align: 'center' },
      content: { x: 0.15, y: 0.52, width: 0.7, height: 0.18, kind: 'text', align: 'center' },
      offer: { x: 0.25, y: 0.72, width: 0.5, height: 0.12, kind: 'text', align: 'center' },
      footer: { x: 0, y: 0.9, width: 1, height: 0.1, kind: 'text', align: 'center' },
    },
    imageZones: [],
    decorationSlots: ['top-left', 'top-right', 'bottom-left', 'bottom-right', 'behind-hero'],
  },
  'offer-first': {
    id: 'offer-first',
    name: 'Offer First',
    description: 'Big offer block up top for promotions, hero below.',
    safeArea: 0.06,
    zones: {
      offer: { x: 0.1, y: 0.08, width: 0.8, height: 0.22, kind: 'text', align: 'center' },
      hero: { x: 0.08, y: 0.34, width: 0.84, height: 0.3, kind: 'text', align: 'center' },
      content: { x: 0.12, y: 0.66, width: 0.76, height: 0.2, kind: 'text', align: 'center' },
      footer: { x: 0, y: 0.9, width: 1, height: 0.1, kind: 'text', align: 'center' },
    },
    imageZones: [{ x: 0.3, y: 0.86, width: 0.4, height: 0.04, role: 'logo-strip' }],
    decorationSlots: ['top-edge', 'bottom-left', 'bottom-right'],
  },
};

export const listLayoutPresets = () =>
  Object.values(LAYOUT_PRESETS).map(({ id, name, description }) => ({ id, name, description }));

const toRect = (frac, canvas) => ({
  x: Math.round(pct(frac.x) * canvas.width),
  y: Math.round(pct(frac.y) * canvas.height),
  width: Math.round(pct(frac.width) * canvas.width),
  height: Math.round(pct(frac.height) * canvas.height),
});

/**
 * Resolve a preset into absolute geometry for a canvas.
 * Returns { preset, canvas, safeArea: rect, zones: { role: rect+meta },
 *           imageZones: rect[], decorationSlots: string[] }
 */
export const resolveLayout = (presetId, canvas) => {
  const preset = LAYOUT_PRESETS[presetId];
  if (!preset) throw new Error(`Unknown layout preset "${presetId}"`);
  if (!canvas?.width || !canvas?.height) throw new Error('resolveLayout: canvas needs width/height');

  const margin = Math.round(preset.safeArea * Math.min(canvas.width, canvas.height));
  const safeArea = {
    x: margin,
    y: margin,
    width: canvas.width - margin * 2,
    height: canvas.height - margin * 2,
  };

  const zones = {};
  Object.entries(preset.zones).forEach(([role, frac]) => {
    zones[role] = { ...toRect(frac, canvas), role, kind: frac.kind, align: frac.align };
  });

  return {
    presetId: preset.id,
    canvas: { ...canvas },
    safeArea,
    zones,
    imageZones: preset.imageZones.map((z) => ({ ...toRect(z, canvas), role: z.role })),
    decorationSlots: [...preset.decorationSlots],
  };
};

/** True if rect `inner` sits fully inside rect `outer`. */
export const rectInside = (inner, outer) =>
  inner.x >= outer.x &&
  inner.y >= outer.y &&
  inner.x + inner.width <= outer.x + outer.width &&
  inner.y + inner.height <= outer.y + outer.height;

/** Intersection area of two rects (0 when disjoint). */
export const rectOverlapArea = (a, b) => {
  const w = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
  return w > 0 && h > 0 ? w * h : 0;
};
